// API 服务配置和请求方法
import { API_BASE_URL } from '../config/apiConfig';

/**
 * 通用HTTP请求方法
 * @param {string} endpoint - API端点
 * @param {Object} options - 请求选项
 * @returns {Promise} 请求结果
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultOptions = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  };

  try {
    const response = await fetch(url, defaultOptions);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // 检查业务逻辑是否成功
    if (data.success === false) {
      throw new Error(data.message || 'API request failed');
    }
    
    return data;
  } catch (error) {
    console.error(`API request failed: ${url}`, error);
    throw error;
  }
};

/**
 * 教材版本相关API
 */
export const textbookVersionAPI = {
  /**
   * 获取所有教材版本
   * @returns {Promise} 版本列表
   */
  getAll: () => apiRequest('/api/textbooks/publish-versions'),
  
  /**
   * 获取指定版本详情
   * @param {number} id - 版本ID
   * @returns {Promise} 版本详情
   */
  getById: (id) => apiRequest(`/api/textbooks/publish-versions/${id}`),
  
  /**
   * 获取指定版本下的所有教材
   * @param {number} versionId - 版本ID
   * @returns {Promise} 教材列表
   */
  getTextbooks: (versionId) => apiRequest(`/api/textbooks/publish-versions/${versionId}/textbooks`),
};

/**
 * 教材相关API
 */
export const textbookAPI = {
  /**
   * 获取所有教材
   * @returns {Promise} 教材列表
   */
  getAll: () => apiRequest('/api/textbooks/textbooks'),
  
  /**
   * 获取指定教材详情
   * @param {number} id - 教材ID
   * @returns {Promise} 教材详情
   */
  getById: (id) => apiRequest(`/api/textbooks/textbooks/${id}`),
  
  /**
   * 获取指定教材下的所有单元
   * @param {number} textbookId - 教材ID
   * @returns {Promise} 单元列表
   */
  getUnits: (textbookId) => apiRequest(`/api/textbooks/textbooks/${textbookId}/units`),
};

/**
 * 单元相关API
 */
export const unitAPI = {
  /**
   * 获取所有单元
   * @returns {Promise} 单元列表
   */
  getAll: () => apiRequest('/api/textbooks/units'),
  
  /**
   * 获取指定单元详情
   * @param {number} id - 单元ID
   * @returns {Promise} 单元详情
   */
  getById: (id) => apiRequest(`/api/textbooks/units/${id}`),
  
  /**
   * 获取指定单元下的所有课时
   * @param {number} unitId - 单元ID
   * @returns {Promise} 课时列表
   */
  getLessons: (unitId) => apiRequest(`/api/textbooks/units/${unitId}/lessons`),
};

/**
 * 课时相关API
 */
export const lessonAPI = {
  /**
   * 获取所有课时
   * @returns {Promise} 课时列表
   */
  getAll: () => apiRequest('/api/textbooks/lessons'),
  
  /**
   * 获取指定课时详情
   * @param {number} id - 课时ID
   * @returns {Promise} 课时详情
   */
  getById: (id) => apiRequest(`/api/textbooks/lessons/${id}`),
  
  /**
   * 获取指定课时的所有词汇
   * @param {number} lessonId - 课时ID
   * @returns {Promise} 词汇列表
   */
  getWords: (lessonId) => apiRequest(`/api/textbooks/lessons/${lessonId}/words`),
};

/**
 * 教材单词相关API
 */
export const textbookWordAPI = {
  /**
   * 获取教材单词列表
   * @param {number} page - 页码
   * @param {number} pageSize - 每页数量
   * @returns {Promise} 单词列表
   */
  getList: (page = 1, pageSize = 10) => 
    apiRequest(`/api/textbookwords?page=${page}&pageSize=${pageSize}`),
  
  /**
   * 搜索教材单词
   * @param {string} keyword - 搜索关键词
   * @returns {Promise} 搜索结果
   */
  search: (keyword) => apiRequest(`/api/textbookwords/search?q=${encodeURIComponent(keyword)}`),
  
  /**
   * 获取指定单词详情
   * @param {number} id - 单词ID
   * @returns {Promise} 单词详情
   */
  getById: (id) => apiRequest(`/api/textbookwords/${id}`),
};

/**
 * 语音评测相关API
 */
export const voiceAPI = {
  /**
   * 获取支持的评测类型
   * @returns {Promise} 评测类型列表
   */
  getTypes: () => apiRequest('/api/voice/types'),
  
  /**
   * 语音评测
   * @param {FormData} formData - 包含音频文件和评测参数的表单数据
   * @param {AbortSignal} signal - 用于取消请求的信号
   * @returns {Promise} 评测结果
   */
  evaluate: async (formData, signal) => {
    const url = `${API_BASE_URL}/api/voice/evaluate`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        signal: signal
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Voice evaluation failed: ${url}`, error);
      throw error;
    }
  }
};

/**
 * 视频标题相关API
 */
export const videoTitleAPI = {
  /**
   * 根据一级标题ID获取二级和三级标题
   * @param {number} firstTitleId - 一级标题ID (1-5: 好篇阅读/培优精讲/专项突破/备考攻略/资源下载)
   * @returns {Promise} 标题列表数据
   */
  getSecondTitles: (firstTitleId) => 
    apiRequest(`/api/video/titles/first/${firstTitleId}/seconds`),
};

/**
 * 视频内容相关API
 */
export const videoContentAPI = {
  /**
   * 获取视频内容列表
   * @param {number} firstTitleId - 一级标题ID
   * @param {number} secondTitleId - 二级标题ID（必填）
   * @param {number} thirdTitleId - 三级标题ID（可选）
   * @returns {Promise} 视频内容列表
   */
  getContents: (firstTitleId, secondTitleId, thirdTitleId = null) => {
    const params = new URLSearchParams({
      first_title_id: firstTitleId,
      second_title_id: secondTitleId,
    });
    
    if (thirdTitleId !== null) {
      params.append('third_title_id', thirdTitleId);
    }
    
    return apiRequest(`/api/video/contents?${params.toString()}`);
  },
};

/**
 * 系统相关API
 */
export const systemAPI = {
  /**
   * 健康检查
   * @returns {Promise} 系统状态
   */
  healthCheck: () => apiRequest('/health'),

  /**
   * 上报用户访问模块
   * @param {string} moduleName - 模块名称
   * @param {string} modulePath - 模块路径
   */
  trackModuleVisit: async (moduleName, modulePath) => {
    const url = `${API_BASE_URL}/api/user/module/visit`;
    const data = {
      module_name: moduleName,
      module_path: modulePath,
      timestamp: Date.now(),
      visit_time: new Date().toISOString()
    };

    const headers = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
    } catch (error) {
      // 静默失败，不影响用户体验
    }
  },

  /**
   * 上报模块结束（页面关闭或超时）
   */
  reportModuleEnd: () => {
    const url = `${API_BASE_URL}/api/user/module/end`;
    const data = { timestamp: Date.now() };

    const headers = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // 优先使用 fetch + keepalive，确保页面关闭时请求不中断
    fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      keepalive: true
    }).catch(() => {
      // 降级重试 sendBeacon
      if (navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      }
    });
  },

  /**
   * 上报用户资源行为（观看视频/下载资源）
   * @param {string} actionType - 行为类型：'watch' (观看视频) | 'download' (下载资源)
   * @param {string} resourceName - 资源名称
   * @param {number} resourceId - 资源ID（可选）
   */
  trackResourceAction: async (actionType, resourceName, resourceId = null) => {
    const url = `${API_BASE_URL}/api/user/resource/action`;
    const data = {
      action_type: actionType,
      resource_name: resourceName,
      resource_id: resourceId,
      timestamp: Date.now(),
      action_time: new Date().toISOString()
    };

    const headers = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
    } catch (error) {
      // 静默失败，不影响用户体验
    }
  }
};

/**
 * 用户统计相关API
 */
export const userStatsAPI = {
  /**
   * 查询用户使用统计
   * @param {Object} params - 查询参数
   * @param {string} params.start_date - 开始日期，格式：YYYY-MM-DD
   * @param {string} params.end_date - 结束日期，格式：YYYY-MM-DD
   * @param {number} params.category - 类别筛选（0=全部，1=使用模块，2=观看视频，3=下载资源，4=语音评测）
   * @returns {Promise} 统计结果
   */
  getUserStats: (params = {}) => {
    // 过滤掉空值参数
    const filteredParams = new URLSearchParams();
    if (params.start_date) filteredParams.append('start_date', params.start_date);
    if (params.end_date) filteredParams.append('end_date', params.end_date);
    if (params.category && params.category > 0) filteredParams.append('category', params.category);
    
    const queryString = filteredParams.toString();
    return apiRequest(`/api/user/stats${queryString ? `?${queryString}` : ''}`);
  },
};

// 默认导出主要使用的API
export default {
  textbookVersionAPI,
  textbookAPI,
  unitAPI,
  lessonAPI,
  textbookWordAPI,
  voiceAPI,
  videoTitleAPI,
  videoContentAPI,
  systemAPI,
  userStatsAPI,
};
